'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, VStack, HStack, Divider } from '@terra/ui-styled-system';
import { Text } from '../../components/text';
import { IconButton } from '../../components/icon-button';
import { Input } from '../../components/input';
import { Scroll } from '../Scroll';
import { Collapsible } from '../../components/collapsible';
import { Checkbox } from '../../components/checkbox';
import {
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  CopyMinusIcon,
  CopyPlusIcon,
  Search01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';

export type TreeNode<T = unknown> = {
  id: string;
  name: string;
  level: number;
  children: TreeNode<T>[];
  parent?: TreeNode<T>;
  isExpanded: boolean;
  data?: T; // Custom data attached to the node
  icon?: React.ReactNode; // Custom icon for the node
  badge?: string | number; // Optional badge content
};
const defaultTreeSearchFunction = <T,>(n: TreeNode<T>, term: string): boolean =>
  n.name.toLowerCase().includes(term.toLowerCase());

// Generic tree type
export type TreeData<T = unknown> = {
  roots: TreeNode<T>[];
  nodeMap: Map<string, TreeNode<T>>;
};
interface TreeProps<T = unknown> {
  data: TreeNode<T>[];
  selectedNodes: string[];
  onSelectionChange: (selectedNodes: string[]) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  /**
   * Optional mapping of node IDs to custom counts.
   * When provided, these counts override the default badge content.
   */
  countMapping?: Record<string, number>;
  /**
   * Selection mode for the tree
   * - 'multiple': Show checkboxes, allow multiple selections (default)
   * - 'single': No checkboxes, VSCode-like single selection with highlighting
   */
  selectionMode?: 'multiple' | 'single';
  /**
   * When true, the panel takes full width instead of fixed width.
   * Useful for resizable panel contexts.
   */
  fullWidth?: boolean;
  /**
   * Title for the tree panel
   */
  title?: string;
  /**
   * Placeholder text for the search input
   */
  searchPlaceholder?: string;
  /**
   * Empty state message when no data is available
   */
  emptyMessage?: string;
  /**
   * Custom render function for node content
   */
  renderNode?: (node: TreeNode<T>) => React.ReactNode;
  /**
   * Custom search function for filtering nodes
   */
  searchFunction?: (node: TreeNode<T>, searchTerm: string) => boolean;
}
interface TreeNodeComponentProps<T = unknown> {
  node: TreeNode<T>;
  selectedNodes: string[];
  onToggleSelection: (nodeId: string, checked: boolean) => void;
  onToggleExpanded: (nodeId: string) => void;
  level?: number;
  searchTerm?: string;
  countMapping?: Record<string, number>;
  selectionMode?: 'multiple' | 'single';
  renderNode?: (node: TreeNode<T>) => React.ReactNode;
  searchFunction?: (node: TreeNode<T>, searchTerm: string) => boolean;
}

function TreeNodeComponent<T = unknown>({
  node,
  selectedNodes,
  onToggleSelection,
  onToggleExpanded,
  level = 0,
  searchTerm = '',
  countMapping,
  selectionMode = 'multiple',
  renderNode,
  searchFunction,
}: TreeNodeComponentProps<T>) {
  const isSelected = selectedNodes.includes(node.id);
  const hasChildren = node.children && node.children.length > 0;
  // Calculate indeterminate state: some but not all children are selected
  const getSelectionState = useMemo(() => {
    // If this node is explicitly selected, it should be checked
    if (isSelected) {
      return { isSelected: true, isIndeterminate: false };
    }

    if (!hasChildren) {
      return { isSelected: false, isIndeterminate: false };
    }

    // Check if any descendants are selected (recursively)
    const areAnyDescendantsSelected = (n: TreeNode<T>): boolean => {
      if (!n.children || n.children.length === 0) {
        return selectedNodes.includes(n.id);
      }

      return n.children.some(
        (child) =>
          selectedNodes.includes(child.id) || areAnyDescendantsSelected(child),
      );
    };

    const anyDescendantsSelected = areAnyDescendantsSelected(node);

    if (anyDescendantsSelected) {
      return { isSelected: false, isIndeterminate: true };
    } else {
      return { isSelected: false, isIndeterminate: false };
    }
  }, [hasChildren, node, selectedNodes, isSelected]);
  const { isSelected: finalIsSelected, isIndeterminate } = getSelectionState;
  const paddingLeft = level * 8;
  const matchesSelf = useMemo(() => {
    if (!searchTerm) return true;
    const searchFn = searchFunction ?? defaultTreeSearchFunction;

    return searchFn(node, searchTerm);
  }, [node, searchTerm, searchFunction]);
  const matchesDescendant = useMemo(() => {
    if (!searchTerm) return false;
    const searchFn = searchFunction ?? defaultTreeSearchFunction;
    const dfs = (n: TreeNode<T>): boolean => {
      if (!n.children || n.children.length === 0) return false;
      for (const c of n.children) {
        if (searchFn(c, searchTerm)) return true;
        if (dfs(c)) return true;
      }

      return false;
    };

    return dfs(node);
  }, [node, searchTerm, searchFunction]);
  const shouldRender = matchesSelf || matchesDescendant;

  if (!shouldRender) {
    return null;
  }

  return (
    <Box>
      {/* Node Row */}
      <Box>
        <HStack
          gap={2}
          alignItems="center"
          py={0.5}
          px={1}
          _hover={{ bg: 'bg.subtle' }}
          borderRadius="md"
          cursor="pointer"
          onClick={(e) => {
            // Don't interfere with expand/collapse buttons or other interactive elements
            const target = e.target as HTMLElement;

            if (target.closest('button') || target.closest('[role="button"]')) {
              return;
            }

            // In single mode, prevent any complex selection logic
            if (selectionMode === 'single') {
              e.stopPropagation();
              onToggleSelection(node.id, !finalIsSelected);

              return;
            }

            // Multiple selection mode
            onToggleSelection(node.id, !finalIsSelected);
          }}
          minH="24px"
        >
          {/* Fixed-width spacer to enforce visual indentation */}
          <Box
            style={{ width: `${paddingLeft}px`, minWidth: `${paddingLeft}px` }}
            flexShrink={0}
            aria-hidden
          />

          {/* Expand/Collapse Button */}
          <Box w="20px" display="flex" justifyContent="center" flexShrink={0}>
            {hasChildren ? (
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={node.isExpanded ? 'collapse' : 'expand'}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpanded(node.id);
                }}
              >
                {node.isExpanded ? (
                  <ArrowDown01Icon size={14} />
                ) : (
                  <ArrowRight01Icon size={14} />
                )}
              </IconButton>
            ) : (
              <Box w="20px" />
            )}
          </Box>

          {/* Selection Checkbox - only show in multiple selection mode */}
          {selectionMode === 'multiple' && (
            <Checkbox
              checked={isIndeterminate ? 'indeterminate' : finalIsSelected}
              onCheckedChange={(details) =>
                onToggleSelection(node.id, !!details.checked)
              }
              size="sm"
              onClick={(e) => e.stopPropagation()}
              flexShrink={0}
            />
          )}

          {/* Node Icon */}
          {node.icon && (
            <Box
              display="flex"
              alignItems="center"
              flexShrink={0}
              color="fg.muted"
            >
              {node.icon}
            </Box>
          )}

          {/* Node Content */}
          <HStack flex={1} gap={2} alignItems="center" minW={0}>
            {renderNode ? (
              renderNode(node)
            ) : (
              <Text
                fontSize="xs"
                fontWeight={
                  selectionMode === 'single' && finalIsSelected
                    ? 'semibold'
                    : 'normal'
                }
                color={
                  selectionMode === 'single' && finalIsSelected
                    ? 'colorPalette.emphasized'
                    : 'fg.default'
                }
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                title={node.name}
                flex={1}
              >
                {node.name}
              </Text>
            )}

            {/* Badge */}
            {(() => {
              const customCount = countMapping?.[node.id];
              const hasCustomCount = customCount !== undefined;
              const displayCount = hasCustomCount ? customCount : node.badge;
              // Show badge if: has custom count (including 0) OR badge content exists
              const shouldShowBadge =
                hasCustomCount || displayCount !== undefined;

              return shouldShowBadge ? (
                <Text
                  fontSize="xs"
                  color="fg.muted"
                  bg="bg.muted"
                  px={1.5}
                  py={0.5}
                  borderRadius="full"
                  flexShrink={0}
                  fontWeight="medium"
                  minW="auto"
                >
                  {displayCount}
                </Text>
              ) : null;
            })()}
          </HStack>
        </HStack>
      </Box>

      {/* Children Nodes */}
      {hasChildren && node.isExpanded && (
        <Box>
          {node.children.map((child) => (
            <TreeNodeComponent
              key={child.id}
              node={child}
              selectedNodes={selectedNodes}
              onToggleSelection={onToggleSelection}
              onToggleExpanded={onToggleExpanded}
              level={level + 1}
              searchTerm={searchTerm}
              countMapping={countMapping}
              selectionMode={selectionMode}
              renderNode={renderNode}
              searchFunction={searchFunction}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

interface TreeContentProps<T = unknown> {
  tree: TreeData<T>;
  selectedNodes: string[];
  selectedCount: number;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  handleExpandAll: () => void;
  handleCollapseAll: () => void;
  handleClearSelection: () => void;
  handleToggleSelection: (nodeId: string, checked: boolean) => void;
  handleToggleExpanded: (nodeId: string) => void;
  countMapping?: Record<string, number>;
  showCollapseButton?: boolean;
  onToggleCollapse?: () => void;
  hasActiveFilter?: boolean;
  selectionMode?: 'multiple' | 'single';
  title?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  renderNode?: (node: TreeNode<T>) => React.ReactNode;
  searchFunction?: (node: TreeNode<T>, searchTerm: string) => boolean;
}

function TreeContent<T = unknown>({
  tree,
  selectedNodes,
  selectedCount,
  searchTerm,
  setSearchTerm,
  handleExpandAll,
  handleCollapseAll,
  handleClearSelection,
  handleToggleSelection,
  handleToggleExpanded,
  countMapping,
  showCollapseButton = true,
  onToggleCollapse,
  hasActiveFilter = false,
  selectionMode = 'multiple',
  title = 'Tree',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items found',
  renderNode,
  searchFunction,
}: TreeContentProps<T>) {
  return (
    <VStack
      w="full"
      h="full"
      minH="0"
      gap={2}
      pt={0}
      px={3}
      pb={3}
      alignItems="stretch"
    >
      <HStack justifyContent="space-between" alignItems="center">
        <Text fontSize="xs" fontWeight="semibold" color="fg.default">
          {title}
        </Text>
        <HStack gap={1} alignItems="center">
          <IconButton
            aria-label="Expand all"
            variant="ghost"
            size="xs"
            onClick={handleExpandAll}
          >
            <CopyPlusIcon size={12} />
          </IconButton>
          <IconButton
            aria-label="Collapse all"
            variant="ghost"
            size="xs"
            onClick={handleCollapseAll}
          >
            <CopyMinusIcon size={12} />
          </IconButton>
          <IconButton
            aria-label="Clear selection"
            size="xs"
            variant="ghost"
            colorPalette="red"
            onClick={handleClearSelection}
            disabled={selectedCount === 0}
          >
            <Cancel01Icon size={12} />
          </IconButton>
          {showCollapseButton && onToggleCollapse && (
            <Box position="relative">
              <IconButton
                variant="ghost"
                size="xs"
                onClick={onToggleCollapse}
                aria-label="Collapse tree"
              >
                <ArrowLeft01Icon size={12} />
              </IconButton>
              {hasActiveFilter && (
                <Box
                  position="absolute"
                  top={0}
                  right={0}
                  w="6px"
                  h="6px"
                  bg="colorPalette.emphasized"
                  borderRadius="full"
                  borderWidth="1px"
                  borderColor="bg.canvas"
                />
              )}
            </Box>
          )}
        </HStack>
      </HStack>

      <Box position="relative">
        <Box
          position="absolute"
          left={3}
          top="50%"
          transform="translateY(-50%)"
          zIndex={1}
          color="fg.muted"
        >
          <Search01Icon size={14} />
        </Box>
        <Input
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          pl="32px"
          size="sm"
          bg="bg.default"
        />
      </Box>

      <Divider />

      <Scroll orientation="vertical" showScrollbar={false} flex={1} minH="0">
        {tree.roots.length === 0 ? (
          <VStack gap={3} alignItems="center" justifyContent="center" py={8}>
            <Text fontSize="xs" color="fg.muted" textAlign="center">
              {emptyMessage}
            </Text>
          </VStack>
        ) : (
          <VStack gap={0} alignItems="stretch">
            {tree.roots.map((rootNode) => (
              <TreeNodeComponent
                key={rootNode.id}
                node={rootNode}
                selectedNodes={selectedNodes}
                onToggleSelection={handleToggleSelection}
                onToggleExpanded={handleToggleExpanded}
                level={0}
                searchTerm={searchTerm}
                countMapping={countMapping}
                selectionMode={selectionMode}
                renderNode={renderNode}
                searchFunction={searchFunction}
              />
            ))}
          </VStack>
        )}
      </Scroll>
    </VStack>
  );
}

export function Tree<T = unknown>({
  data,
  selectedNodes,
  onSelectionChange,
  isCollapsed = false,
  onToggleCollapse,
  countMapping,
  fullWidth = false,
  selectionMode = 'multiple',
  title = 'Tree',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items found',
  renderNode,
  searchFunction,
}: TreeProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [tree, setTree] = useState<TreeData<T>>(() => {
    // Convert flat data to tree structure
    const nodeMap = new Map<string, TreeNode<T>>();
    const roots: TreeNode<T>[] = [];

    // First pass: create all nodes
    data.forEach((node) => {
      nodeMap.set(node.id, { ...node, children: [] });
    });
    // Second pass: build parent-child relationships
    data.forEach((node) => {
      const treeNode = nodeMap.get(node.id);

      if (!treeNode) {
        return;
      }

      if (node.parent) {
        const parentNode = nodeMap.get(node.parent.id);

        if (parentNode) {
          parentNode.children.push(treeNode);
          treeNode.parent = parentNode;
        }
      } else {
        roots.push(treeNode);
      }
    });

    return { roots, nodeMap };
  });

  // Rebuild tree when data changes
  useEffect(() => {
    setTree((prevTree) => {
      const nodeMap = new Map<string, TreeNode<T>>();
      const roots: TreeNode<T>[] = [];

      // First pass: create all nodes
      data.forEach((node) => {
        nodeMap.set(node.id, { ...node, children: [] });
      });
      // Second pass: build parent-child relationships
      data.forEach((node) => {
        const treeNode = nodeMap.get(node.id);

        if (!treeNode) {
          return;
        }

        if (node.parent) {
          const parentNode = nodeMap.get(node.parent.id);

          if (parentNode) {
            parentNode.children.push(treeNode);
            treeNode.parent = parentNode;
          }
        } else {
          roots.push(treeNode);
        }
      });
      // Preserve existing expanded state when rebuilding the tree
      if (prevTree.nodeMap.size > 0) {
        nodeMap.forEach((newNode, nodeId) => {
          const existingNode = prevTree.nodeMap.get(nodeId);

          if (existingNode) {
            newNode.isExpanded = existingNode.isExpanded;
          }
        });
      }

      return { roots, nodeMap };
    });
  }, [data]);
  const handleToggleSelection = useCallback(
    (nodeId: string, checked: boolean) => {
      const node = tree.nodeMap.get(nodeId);

      if (!node) return;
      if (selectionMode === 'single') {
        // Simple single selection logic
        if (checked) {
          // Select only this node (deselect others)
          onSelectionChange([nodeId]);
        } else {
          // Deselect this node
          onSelectionChange([]);
        }

        return;
      }

      // Multiple selection mode (original complex logic)
      let newSelectedNodes = [...selectedNodes];

      if (checked) {
        // Add the selected node
        if (!newSelectedNodes.includes(nodeId)) {
          newSelectedNodes.push(nodeId);
        }

        // Remove all descendants when selecting a parent
        const removeDescendants = (n: TreeNode<T>) => {
          if (n.children) {
            n.children.forEach((child) => {
              newSelectedNodes = newSelectedNodes.filter(
                (id) => id !== child.id,
              );
              removeDescendants(child);
            });
          }
        };

        if (node.children) {
          removeDescendants(node);
        }

        // Remove selected ancestors (drill down behavior)
        let currentParent = node.parent;

        while (currentParent) {
          const parentId = currentParent.id;
          const nextParent = currentParent.parent;

          if (newSelectedNodes.includes(parentId)) {
            newSelectedNodes = newSelectedNodes.filter((id) => id !== parentId);
          }

          currentParent = nextParent;
        }
      } else {
        // Remove the unselected node
        newSelectedNodes = newSelectedNodes.filter((id) => id !== nodeId);
        // Remove all descendants
        const removeDescendants = (n: TreeNode<T>) => {
          newSelectedNodes = newSelectedNodes.filter((id) => id !== n.id);
          if (n.children) {
            n.children.forEach(removeDescendants);
          }
        };

        if (node.children) {
          node.children.forEach(removeDescendants);
        }
      }

      onSelectionChange(newSelectedNodes);
    },
    [selectedNodes, onSelectionChange, tree.nodeMap, selectionMode],
  );
  const handleToggleExpanded = useCallback((nodeId: string) => {
    setTree((prevTree) => {
      const newNodeMap = new Map<string, TreeNode<T>>();
      const cloneNode = (node: TreeNode<T>): TreeNode<T> => {
        const cloned = {
          ...node,
          children: [],
          parent: undefined,
        };

        if (node.id === nodeId) {
          cloned.isExpanded = !node.isExpanded;
        }

        return cloned;
      };

      prevTree.nodeMap.forEach((node, id) => {
        newNodeMap.set(id, cloneNode(node));
      });
      newNodeMap.forEach((node) => {
        const originalNode = prevTree.nodeMap.get(node.id);

        if (originalNode?.parent) {
          const parentNode = newNodeMap.get(originalNode.parent.id);

          if (parentNode) {
            node.parent = parentNode;
          }
        }

        if (originalNode?.children) {
          node.children = originalNode.children
            .map((child) => newNodeMap.get(child.id))
            .filter((child): child is TreeNode<T> => child !== undefined);
        }
      });
      const newRoots = prevTree.roots
        .map((root) => newNodeMap.get(root.id))
        .filter((root): root is TreeNode<T> => root !== undefined);
      const newTree = {
        roots: newRoots,
        nodeMap: newNodeMap,
      };

      return newTree;
    });
  }, []);
  const handleExpandAll = useCallback(() => {
    setTree((prevTree) => {
      const newNodeMap = new Map<string, TreeNode<T>>();

      prevTree.nodeMap.forEach((node, id) => {
        newNodeMap.set(id, { ...node, isExpanded: true });
      });
      newNodeMap.forEach((node) => {
        const originalNode = prevTree.nodeMap.get(node.id);

        if (originalNode?.parent) {
          const parentNode = newNodeMap.get(originalNode.parent.id);

          if (parentNode) {
            node.parent = parentNode;
          }
        }

        if (originalNode?.children) {
          node.children = originalNode.children
            .map((child) => newNodeMap.get(child.id))
            .filter((child): child is TreeNode<T> => child !== undefined);
        }
      });

      return {
        roots: prevTree.roots
          .map((root) => newNodeMap.get(root.id))
          .filter((root): root is TreeNode<T> => root !== undefined),
        nodeMap: newNodeMap,
      };
    });
  }, []);
  const handleCollapseAll = useCallback(() => {
    setTree((prevTree) => {
      const newNodeMap = new Map<string, TreeNode<T>>();

      prevTree.nodeMap.forEach((node, id) => {
        newNodeMap.set(id, { ...node, isExpanded: false });
      });
      newNodeMap.forEach((node) => {
        const originalNode = prevTree.nodeMap.get(node.id);

        if (originalNode?.parent) {
          const parentNode = newNodeMap.get(originalNode.parent.id);

          if (parentNode) {
            node.parent = parentNode;
          }
        }

        if (originalNode?.children) {
          node.children = originalNode.children
            .map((child) => newNodeMap.get(child.id))
            .filter((child): child is TreeNode<T> => child !== undefined);
        }
      });

      return {
        roots: prevTree.roots
          .map((root) => newNodeMap.get(root.id))
          .filter((root): root is TreeNode<T> => root !== undefined),
        nodeMap: newNodeMap,
      };
    });
  }, []);
  const handleClearSelection = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);
  const selectedCount = selectedNodes.length;
  const hasActiveFilter = selectedCount > 0 || searchTerm.length > 0;

  return (
    <Box
      height="full"
      borderRightWidth="1px"
      borderColor="border.subtle"
      bg="bg.canvas"
    >
      {fullWidth ? (
        // Full width mode - bypass collapsible wrapper entirely
        <TreeContent
          tree={tree}
          selectedNodes={selectedNodes}
          selectedCount={selectedCount}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleExpandAll={handleExpandAll}
          handleCollapseAll={handleCollapseAll}
          handleClearSelection={handleClearSelection}
          handleToggleSelection={handleToggleSelection}
          handleToggleExpanded={handleToggleExpanded}
          countMapping={countMapping}
          showCollapseButton={false}
          hasActiveFilter={hasActiveFilter}
          selectionMode={selectionMode}
          title={title}
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
          renderNode={renderNode}
          searchFunction={searchFunction}
        />
      ) : (
        // Original collapsible mode
        <Collapsible.Root
          open={!isCollapsed}
          onOpenChange={({ open: _open }) => onToggleCollapse?.()}
        >
          <Box
            w={isCollapsed ? '40px' : '400px'}
            h="full"
            transition="width 0.3s ease-in-out"
          >
            {isCollapsed ? (
              <Box
                h="full"
                display="flex"
                alignItems="flex-start"
                justifyContent="center"
                pt={0}
              >
                <Box position="relative">
                  <Collapsible.Trigger asChild>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      aria-label="Expand tree"
                    >
                      <ArrowRight01Icon size={12} />
                    </IconButton>
                  </Collapsible.Trigger>
                  {hasActiveFilter && (
                    <Box
                      position="absolute"
                      top={0}
                      right={0}
                      w="6px"
                      h="6px"
                      bg="colorPalette.emphasized"
                      borderRadius="full"
                      borderWidth="1px"
                      borderColor="bg.canvas"
                    />
                  )}
                </Box>
              </Box>
            ) : (
              <Collapsible.Content>
                <Box w="400px">
                  <TreeContent
                    tree={tree}
                    selectedNodes={selectedNodes}
                    selectedCount={selectedCount}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    handleExpandAll={handleExpandAll}
                    handleCollapseAll={handleCollapseAll}
                    handleClearSelection={handleClearSelection}
                    handleToggleSelection={handleToggleSelection}
                    handleToggleExpanded={handleToggleExpanded}
                    countMapping={countMapping}
                    showCollapseButton={true}
                    onToggleCollapse={onToggleCollapse}
                    hasActiveFilter={hasActiveFilter}
                    selectionMode={selectionMode}
                    title={title}
                    searchPlaceholder={searchPlaceholder}
                    emptyMessage={emptyMessage}
                    renderNode={renderNode}
                    searchFunction={searchFunction}
                  />
                </Box>
              </Collapsible.Content>
            )}
          </Box>
        </Collapsible.Root>
      )}
    </Box>
  );
}
